import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { CheckCircle2, Search, DollarSign, ArrowLeft, Users, Info, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const MONTHS = ['','Januari','Februari','Maret','April','Mei','Juni',
                 'Juli','Agustus','September','Oktober','November','Desember'];
const fmt = n => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

export default function KelilingPage() {
    const { hasRole } = useAuth();
    const navigate = useNavigate();
    const now = new Date();
    const [citizens, setCitizens] = useState([]);
    const [paidSet, setPaidSet] = useState(new Set());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [showPayForm, setShowPayForm] = useState(false);
    const [selected, setSelected] = useState(null);
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [filterMode, setFilterMode] = useState('unpaid');
    const [totalCollected, setTotalCollected] = useState(0);
    const [smartRate, setSmartRate] = useState(null);
    const [smartLoading, setSmartLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cRes, iRes] = await Promise.all([
                axios.get(`${API}/citizens`, { params: { limit: 500 }, withCredentials: true }),
                axios.get(`${API}/finance/iuran`, { params: { month, year, limit: 500 }, withCredentials: true })
            ]);
            setCitizens(cRes.data.data || []);
            const paid = new Set(iRes.data.data?.map(i => i.citizen_id) || []);
            setPaidSet(paid);
            const total = iRes.data.data?.reduce((sum, i) => sum + (i.amount || 0), 0) || 0;
            setTotalCollected(total);
        } catch { toast.error('Gagal memuat data'); }
        setLoading(false);
    }, [month, year]);

    useEffect(() => { load(); }, [load]);

    const fetchSmartRate = async (citizenId, m, y) => {
        setSmartLoading(true);
        setSmartRate(null);
        try {
            const res = await axios.get(`${API}/finance/iuran/citizen-rate/${citizenId}?month=${m}&year=${y}`, { withCredentials: true });
            setSmartRate(res.data);
            setAmount(String(res.data.minimum_payment));
        } catch { toast.error('Gagal mengambil tarif'); }
        setSmartLoading(false);
    };

    const openPay = (c) => {
        setSelected(c);
        setAmount('');
        setNotes('');
        setSmartRate(null);
        setShowPayForm(true);
        fetchSmartRate(c.id, month, year);
    };

    const isValidAmount = () => {
        if (!smartRate) return Number(amount) > 0;
        const base = smartRate.base_rate;
        const amt = Number(amount);
        if (base === 0) return amt >= 0;
        return amt >= base && amt % base === 0;
    };

    const getAmountError = () => {
        if (!smartRate || !amount) return '';
        const base = smartRate.base_rate;
        const amt = Number(amount);
        if (base === 0) return '';
        if (amt < base) return `Minimal: ${fmt(base)}`;
        if (amt % base !== 0) return `Harus kelipatan ${fmt(base)}`;
        return '';
    };

    const getMonthsCovered = () => {
        if (!smartRate || !amount) return [];
        const base = smartRate.base_rate;
        const amt = Number(amount);
        if (base === 0 || amt < base) return [];
        const count = Math.floor(amt / base);
        const unpaid = smartRate.unpaid_months || [];
        const allMonths = [...unpaid];
        let lastMonth = unpaid.length > 0 ? unpaid[unpaid.length - 1] : { month, year };
        while (allMonths.length < count) {
            let nm = lastMonth.month + 1, ny = lastMonth.year;
            if (nm > 12) { nm = 1; ny++; }
            allMonths.push({ month: nm, year: ny });
            lastMonth = { month: nm, year: ny };
        }
        return allMonths.slice(0, count);
    };

    const handlePay = async () => {
        if (!selected || !amount) return;
        if (!isValidAmount()) { toast.error(getAmountError() || 'Nominal tidak valid'); return; }
        setSaving(true);
        try {
            const amt = Number(amount);
            const base = smartRate?.base_rate || amt;
            const monthsCovered = smartRate ? getMonthsCovered() : [{ month, year }];

            if (monthsCovered.length > 1) {
                await axios.post(`${API}/finance/iuran/multi`, {
                    citizen_id: selected.id, kk_number: selected.kk_number,
                    head_of_family: selected.head_of_family,
                    start_month: monthsCovered[0]?.month || month,
                    start_year: monthsCovered[0]?.year || year,
                    amount_per_month: base,
                    months_to_cover: monthsCovered,
                    notes
                }, { withCredentials: true });
                toast.success(`${monthsCovered.length} bulan iuran ${selected.head_of_family} dicatat!`);
            } else {
                const target = monthsCovered[0] || { month, year };
                await axios.post(`${API}/finance/iuran`, {
                    citizen_id: selected.id, kk_number: selected.kk_number,
                    head_of_family: selected.head_of_family,
                    month: target.month, year: target.year,
                    amount: amt, notes
                }, { withCredentials: true });
                toast.success(`Iuran ${selected.head_of_family} dicatat!`);
            }
            setPaidSet(prev => new Set([...prev, selected.id]));
            setTotalCollected(prev => prev + amt);
            setShowPayForm(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal mencatat iuran');
        }
        setSaving(false);
    };

    const filtered = citizens.filter(c => {
        const matchQ = !q || c.head_of_family.toLowerCase().includes(q.toLowerCase()) ||
                       c.address.toLowerCase().includes(q.toLowerCase());
        const isPaid = paidSet.has(c.id);
        if (filterMode === 'paid') return matchQ && isPaid;
        if (filterMode === 'unpaid') return matchQ && !isPaid;
        return matchQ;
    });

    const paidCount = paidSet.size;
    const totalCount = citizens.length;
    const progress = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;
    const monthsCovered = getMonthsCovered();
    const amtError = getAmountError();

    return (
        <div className="min-h-screen bg-slate-50" data-testid="keliling-page">
            {/* Sticky header */}
            <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
                <div className="px-4 py-3 max-w-lg mx-auto">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate('/finance')}
                                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors" data-testid="back-btn">
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="font-extrabold text-slate-900 leading-none" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                    Mode Keliling
                                </h1>
                                <p className="text-xs text-slate-500">{MONTHS[month]} {year}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                                <SelectTrigger className="h-8 w-28 text-xs" data-testid="keliling-month">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MONTHS.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)} className="text-xs">{m}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                                <SelectTrigger className="h-8 w-20 text-xs" data-testid="keliling-year">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[now.getFullYear(), now.getFullYear()-1].map(y => (
                                        <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-green-700 font-semibold">{paidCount} lunas</span>
                            <span className="font-extrabold text-primary" style={{ fontFamily: 'Plus Jakarta Sans' }}>{progress}%</span>
                            <span className="text-slate-400">{totalCount - paidCount} belum</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #065f46, #0F766E)' }} />
                        </div>
                    </div>

                    {/* Search + filter */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <Input value={q} onChange={e => setQ(e.target.value)}
                                placeholder="Cari nama warga..." className="pl-8 h-8 text-sm"
                                data-testid="keliling-search" />
                        </div>
                        <div className="flex rounded-lg overflow-hidden border border-slate-200">
                            {[['unpaid','Belum'],['paid','Lunas'],['all','Semua']].map(([val, label]) => (
                                <button key={val} onClick={() => setFilterMode(val)}
                                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                                        filterMode === val ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                                    data-testid={`keliling-filter-${val}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="px-4 py-3 max-w-lg mx-auto space-y-2 pb-28">
                {loading ? (
                    <div className="text-center py-16 text-slate-400">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                        <p className="text-sm">Memuat data warga...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16">
                        <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">
                            {filterMode === 'paid' ? 'Belum ada yang lunas bulan ini' :
                             filterMode === 'unpaid' ? 'Semua warga sudah lunas!' :
                             'Tidak ada data warga'}
                        </p>
                    </div>
                ) : filtered.map(c => {
                    const isPaid = paidSet.has(c.id);
                    return (
                        <div key={c.id} data-testid={`keliling-row-${c.id}`}
                            className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                                isPaid ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 shadow-sm'}`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                isPaid ? 'bg-green-200 text-green-800' : 'bg-primary/10 text-primary'}`}>
                                {c.head_of_family.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm truncate ${isPaid ? 'text-green-800' : 'text-slate-800'}`}>
                                    {c.head_of_family}
                                </p>
                                <p className="text-xs text-slate-400 truncate">{c.address}</p>
                                <p className="text-xs text-slate-400 font-mono">{c.members?.length || 0} jiwa</p>
                            </div>
                            {isPaid ? (
                                <div className="flex items-center gap-1.5 text-green-700 font-semibold text-sm flex-shrink-0">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span>Lunas</span>
                                </div>
                            ) : (
                                <button onClick={() => openPay(c)} data-testid={`record-pay-${c.id}`}
                                    className="flex items-center gap-1.5 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 active:scale-95 transition-all flex-shrink-0 shadow-sm">
                                    <DollarSign className="w-4 h-4" />
                                    Catat
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Floating summary */}
            <div className="fixed bottom-4 left-0 right-0 flex justify-center pointer-events-none">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 px-5 py-3 flex items-center gap-4 pointer-events-auto">
                    <div className="text-center">
                        <p className="text-xs text-slate-400 leading-none mb-0.5">Terkumpul</p>
                        <p className="font-extrabold text-primary text-base leading-none" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            Rp {totalCollected.toLocaleString('id-ID')}
                        </p>
                    </div>
                    <div className="w-px h-8 bg-slate-200" />
                    <div className="text-center">
                        <p className="text-xs text-slate-400 leading-none mb-0.5">Progress</p>
                        <p className="font-extrabold text-slate-800 text-base leading-none" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            {paidCount}/{totalCount} KK
                        </p>
                    </div>
                </div>
            </div>

            {/* Payment dialog */}
            <Dialog open={showPayForm} onOpenChange={v => { if (!v) { setShowPayForm(false); setSmartRate(null); setAmount(''); } }}>
                <DialogContent className="max-w-sm mx-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-primary" />
                            Catat Iuran
                        </DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4 py-1">
                            <div className="bg-primary/5 rounded-xl p-3 border border-primary/20">
                                <p className="font-extrabold text-slate-900 text-base" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                    {selected.head_of_family}
                                </p>
                                <p className="text-sm text-slate-500 mt-0.5">{selected.address}</p>
                                <p className="text-xs text-slate-400 font-mono mt-0.5">KK: {selected.kk_number}</p>
                            </div>

                            {/* Smart Rate Info */}
                            {smartLoading && (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    Menghitung tarif...
                                </div>
                            )}
                            {smartRate && !smartLoading && (
                                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-1.5">
                                    <div className="flex items-start gap-2">
                                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm">
                                            <p className="font-semibold text-blue-800">Tarif Iuran</p>
                                            <p className="text-blue-700">Tarif dasar: <span className="font-bold">{fmt(smartRate.base_rate)}/bulan</span></p>
                                            {smartRate.arrears_count > 0 && (
                                                <p className="text-amber-700 font-semibold mt-1">
                                                    Tunggakan: {smartRate.arrears_count} bulan — min. {fmt(smartRate.minimum_payment)}
                                                </p>
                                            )}
                                            {smartRate.arrears_count === 0 && <p className="text-green-700">Tidak ada tunggakan</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1.5">
                                <Label className="font-semibold">Jumlah Iuran (Rp) *</Label>
                                <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                                    step={smartRate?.base_rate || 1000}
                                    min={smartRate?.minimum_payment || 0}
                                    className={`h-12 text-xl font-bold text-center text-primary border-primary/30 ${amtError ? 'border-red-400' : ''}`}
                                    data-testid="keliling-amount-input" />
                                {amtError && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{amtError}</p>}
                            </div>

                            {/* Months coverage */}
                            {smartRate && monthsCovered.length > 0 && isValidAmount() && (
                                <div className="rounded-xl bg-green-50 border border-green-200 p-3">
                                    <p className="text-xs font-semibold text-green-800 mb-1.5">
                                        Mencakup {monthsCovered.length} bulan:
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {monthsCovered.map((slot, i) => (
                                            <span key={i} className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                                {MONTHS[slot.month]} {slot.year}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1.5">
                                <Label className="font-semibold">Catatan</Label>
                                <Input value={notes} onChange={e => setNotes(e.target.value)}
                                    placeholder="Opsional..." className="h-9" />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowPayForm(false)} className="flex-1 h-11">Batal</Button>
                        <Button onClick={handlePay} disabled={saving || !isValidAmount() || !amount}
                            className="flex-1 h-11 bg-primary hover:bg-primary/90 font-bold text-base"
                            data-testid="confirm-keliling-pay">
                            {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Konfirmasi'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
